/**
 * Central icon registry — maps semantic names to icon components.
 * Import from here instead of react-icons directly so that changing
 * an icon only requires editing this file.
 *
 * Usage:
 *   import { AppIcons } from "constants/icons";
 *   <Icon as={AppIcons.workflow} />
 */
import { LuWorkflow, LuLineChart, LuBarChart3, LuPlay, LuTerminal, LuInfo } from "react-icons/lu";
import {
  MdSchedule,
  MdCode,
  MdOutlineFileDownload,
  MdOutlineSegment,
  MdBarChart,
  MdNumbers,
  MdMemory,
  MdEdit,
  MdErrorOutline,
  MdCheckCircle,
  MdInput,
  MdOutlineIntegrationInstructions,
} from "react-icons/md";
import { BsThreeDotsVertical, BsFiletypePng, BsFiletypeJpg, BsRobot } from "react-icons/bs";
import {
  FaChartLine,
  FaPuzzlePiece,
  FaSearch,
  FaArrowLeft,
  FaCheck,
  FaUndo,
  FaLongArrowAltDown,
  FaMinus,
  FaPlus,
  FaRegFileExcel,
  FaFilter,
  FaChevronUp,
  FaChevronDown,
  FaChartPie,
  FaFileWord,
  FaEye,
  FaEyeSlash,
  FaExclamationTriangle,
  FaRegLightbulb,
  FaChevronRight,
  FaHistory,
  FaPause,
  FaTrash,
} from "react-icons/fa";
import { FaTableList } from "react-icons/fa6";
import { TbMathFunction, TbLayoutSidebarRightCollapseFilled, TbBrandPython } from "react-icons/tb";
import { HiOutlineDocumentReport } from "react-icons/hi";
import { HiOutlineDatabase } from "react-icons/hi";
import { TiFlowMerge } from "react-icons/ti";
import { ChevronLeftIcon, ChevronRightIcon, RepeatIcon, ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { IoChatbubble, IoChatbubblesOutline, IoCopyOutline, IoSparklesSharp } from "react-icons/io5";
import { FiCalendar } from "react-icons/fi";
import { PiTarget } from "react-icons/pi";
import { BiSolidNetworkChart } from "react-icons/bi";
import { GiBrain } from "react-icons/gi";
import { FaList } from "react-icons/fa6";

export const AppIcons = {
  analyst: BsRobot,
  workflow: LuWorkflow,
  workflowRun: MdSchedule,
  analystScope: BiSolidNetworkChart,
  analystMemory: MdMemory,
  menuDots: BsThreeDotsVertical,
  analyseData: LuLineChart,
  buildChart: LuBarChart3,
  runWorkflow: LuPlay,
  leftArrow: FaArrowLeft,
  check: FaCheck,
  undo: FaUndo,
  search: FaSearch,
  chart: FaChartLine,
  dataframe: FaTableList,
  loadSkill: FaPuzzlePiece,
  mathFunction: TbMathFunction,
  collapseSidebar: TbLayoutSidebarRightCollapseFilled,
  python: TbBrandPython,
  database: HiOutlineDatabase,
  list: FaList,
  codeAlt: MdCode,
  arrowDown: FaLongArrowAltDown,
  minus: FaMinus,
  plus: FaPlus,
  fileExcel: FaRegFileExcel,
  filePng: BsFiletypePng,
  fileJpg: BsFiletypeJpg,
  flowMerge: TiFlowMerge,
  viewOn: ViewIcon,
  viewOff: ViewOffIcon,
  download: MdOutlineFileDownload,
  copy: IoCopyOutline,
  blocks: MdOutlineSegment,
  filter: FaFilter,
  chevronUp: FaChevronUp,
  chevronDown: FaChevronDown,
  knowledge: GiBrain,
  metric: MdBarChart,
  attribute: FaChartPie,
  timeframe: FiCalendar,
  benchmark: PiTarget,
  measure: MdNumbers,
  edit: MdEdit,
  analystChat: IoChatbubble,
  document: FaFileWord,
  eye: FaEye,
  eyeOff: FaEyeSlash,
  warning: FaExclamationTriangle,
  chevronRight: FaChevronRight,
  lightbulb: FaRegLightbulb,
  checkCircle: MdCheckCircle,
  errorOutline: MdErrorOutline,
  refresh: RepeatIcon,
  terminal: LuTerminal,
  leftIcon: ChevronLeftIcon,
  rightIcon: ChevronRightIcon,
  info: LuInfo,
  delete: FaTrash,
  history: FaHistory,
  pause: FaPause,
  inputs: MdInput,
  chatSessions: IoChatbubblesOutline,
  results: IoSparklesSharp,
  guidance: MdOutlineIntegrationInstructions,
  findings: HiOutlineDocumentReport,
} as const;

export type AppIconKey = keyof typeof AppIcons;
